import { FilterType } from "@app/projects/enumerators";
import { ProjectModule } from "@app/projects/literals";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import { AiChatMessage, AiChatMessageType } from "@app/projects/network-assessment/provider-hub/interfaces";

// Deliberately does NOT start with "The next user message was sent while the user was viewing":
// ChatMessageService.GetCortexPromptAsync keys the providerProfile prompt variable off that prefix, so
// reusing it would tell Quinn a provider is open.
export const APPLIED_FILTERS_PREFIX = "The user was viewing data for the following when they sent the next message:";

// The Summaries tab's network inventory counts come from a separate calculation over different data than the
// run analysis Quinn queries, so tell Quinn why its numbers may not match. Like APPLIED_FILTERS_PREFIX, this must
// not start with the provider-profile prefix. Must match ChatMessageService.SummariesTabViewingContext exactly:
// the backend only lets this exact text through to the step that writes data answers.
export const SUMMARIES_TAB_CONTEXT = "The user sent the next message while viewing the project's Summaries tab. That tab shows network inventory counts (access points, unique NPIs and unique locations by specialty code, county, state, geocode, NPI status and address count) produced by a separate calculation, not the run analysis data Quinn queries. Numbers can legitimately differ from Quinn's because: in its default Network view the tab counts the whole network provider set, not only providers serving this project's service areas and specialty groups; it groups providers by the county where they are located rather than the region set (service area) they serve; it groups by specialty code rather than the project's specialty groups; and it applies provider exclusions as of when the summary was calculated, which may differ from when this run was analyzed. If your answer includes provider, NPI, location or access point counts, or the user asks why numbers differ from the Summaries tab, briefly explain this. You cannot see the Summaries tab's numbers, so never quote or estimate them.";

// The Provider Claims Insights tab labels its claims-volume breakdown "Provider Status", which Quinn would otherwise
// read as network status, so tell Quinn what the tab means by it. Like APPLIED_FILTERS_PREFIX, this must not start
// with the provider-profile prefix. Must match ChatMessageService.ProviderClaimsInsightsTabViewingContext exactly:
// the backend only lets this exact text through to the step that writes data answers.
export const PROVIDER_CLAIMS_INSIGHTS_TAB_CONTEXT = "The user sent the next message while viewing the project's Provider Claims Insights tab. On that tab, \"Provider Status\" means a provider's claims volume label (Core, Standard, Peripheral, RX Only, Insufficient Data or Ghost), assigned separately for each line of business, not network participation, termination or targeting status. The tab counts distinct servicing providers per label. When the user asks about provider status, treat it as the claims volume label, count distinct providers, and use the line of business the user names, or Total if none is named. You cannot see the tab's numbers, so never quote or estimate them.";

/** Resolves a county filter's region code to the county's canonical region set id, or null/undefined if unknown. */
export type CountyRegionSetIdResolver = (countyRegionCode: string) => number | null | undefined;

const findFilterValue = (filters: RegionFilterItem[], filterType: FilterType): string | undefined =>
  filters.find(f => f.filterType === filterType && !!f.filterValue)?.filterValue;

// A county chip carries a region code (e.g. "02110"), but Snowflake's ADEQUACY_REGION_SET_SERVICING_RESULT
// keys counties by canonical region set id, so translate it. The county is the more specific drill-down,
// so it wins over a region filter; a county that can't be resolved is dropped rather than sent as a code.
const getRegionSetId = (filters: RegionFilterItem[], resolveCountyRegionSetId: CountyRegionSetIdResolver): string | undefined => {
  const countyRegionCode = findFilterValue(filters, FilterType.County);
  const countyRegionSetId = countyRegionCode ? resolveCountyRegionSetId(countyRegionCode) : undefined;
  if (countyRegionSetId !== null && countyRegionSetId !== undefined) {
    return countyRegionSetId.toString();
  }
  return findFilterValue(filters, FilterType.Region);
};

/**
 * Builds the System message that tells Quinn which state / region set / specialty group filters are applied
 * to the project view, as raw codes/ids only. Reads the validated filter chips (not the raw URL values) so only
 * filters the user can actually see are sent; a state chip's value is its two-letter abbreviation. Returns
 * undefined when none of those filters are applied.
 */
export const buildAppliedFiltersSystemMessage = (
  filters: RegionFilterItem[],
  resolveCountyRegionSetId: CountyRegionSetIdResolver
): AiChatMessage | undefined => {
  // Fixed order so the message is stable regardless of the order the user drilled in.
  const descriptors: [string, string | undefined][] = [
    ["STATE", findFilterValue(filters, FilterType.State)],
    ["REGION_SET_ID", getRegionSetId(filters, resolveCountyRegionSetId)],
    ["SPECIALTY_GROUP_CODE", findFilterValue(filters, FilterType.Specialty)],
  ];
  const parts = descriptors
    .filter(([, value]) => !!value)
    .map(([description, value]) => `${description} = ${value}`);

  if (parts.length === 0) {
    return undefined;
  }

  return {
    content: [`${APPLIED_FILTERS_PREFIX} ${parts.join("; ")}.`],
    messageType: AiChatMessageType.System,
    timestamp: new Date()
  };
};

/**
 * Builds the System message that explains why Quinn's numbers may differ from the Summaries tab's, or
 * undefined when the user is not on that tab.
 */
export const buildSummariesTabSystemMessage = (activeProjectModule?: ProjectModule): AiChatMessage | undefined =>
  activeProjectModule === "Summary"
    ? { content: [SUMMARIES_TAB_CONTEXT], messageType: AiChatMessageType.System, timestamp: new Date() }
    : undefined;

/**
 * Builds the System message that explains what "Provider Status" means on the Provider Claims Insights tab, or
 * undefined when the user is not on that tab.
 */
export const buildProviderClaimsInsightsTabSystemMessage = (isOnProviderClaimsInsightsTab?: boolean): AiChatMessage | undefined =>
  isOnProviderClaimsInsightsTab
    ? { content: [PROVIDER_CLAIMS_INSIGHTS_TAB_CONTEXT], messageType: AiChatMessageType.System, timestamp: new Date() }
    : undefined;
